/**
 * QuickBooks API Module
 *
 * Handles QuickBooks OAuth integration and invoice operations
 * Following established Silo API patterns
 */

import type { RequestFunction } from './types'

// QuickBooks-specific types
// MUST MATCH BACKEND RESPONSE from /api/quickbooks/oauth/status/
export interface QBConnectionStatus {
  connected: boolean;  // Backend returns "connected" not "is_connected"
  realm_id: string | null;
  expires_at: string | null;
  has_valid_token: boolean;
}

export interface QBOAuthResponse {
  authorization_url: string;
  state: string;
  message: string;
}

export interface QBSyncResult {
  success: boolean;
  invoice_id?: string;
  invoice_url?: string;
  result?: any;
  idempotent?: boolean;
  error?: string;
  message?: string;
}

export interface QBInvoice {
  id: string;
  quickbooks_id: string;
  number: string;
  customer_name: string;
  customer_id: string;
  total: number;
  subtotal: number;
  tax: number;
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'partial';
  due_date: string;
  created_at: string;
  sent_at?: string;
  paid_at?: string;
  payment_terms: string;
  notes?: string;
}

export interface QBDisconnectResult {
  success: boolean;
  message: string;
  user_id: number;
}

export class QuickBooksApi {
  constructor(private makeRequest: RequestFunction) {}

  // ============================================================================
  // CONNECTION MANAGEMENT (PER-USER AUTH REQUIRED)
  // ============================================================================

  async getConnectionStatus(): Promise<QBConnectionStatus> {
    // Per-user connection status - requires JWT authentication
    return this.makeRequest('/api/quickbooks/oauth/status/')
  }

  async initiateOAuth(): Promise<QBOAuthResponse> {
    // OAuth initiate - POST with JWT auth (per-user)
    return this.makeRequest('/api/quickbooks/oauth/initiate/', {
      method: 'POST'
    })
  }

  async disconnect(): Promise<QBDisconnectResult> {
    return this.makeRequest<QBDisconnectResult>('/api/quickbooks/oauth/disconnect/', {
      method: 'POST'
    })
  }

  // ============================================================================
  // INVOICE OPERATIONS
  // ============================================================================

  async syncOrderToQuickBooks(orderId: number): Promise<QBSyncResult> {
    // Use standard makeRequest pattern (proxy will handle Token format conversion)
    return this.makeRequest<QBSyncResult>(`/api/ar/orders/${orderId}/sync_quickbooks/`, {
      method: 'POST'
    })
  }

  async getInvoices(params?: {
    status?: 'draft' | 'sent' | 'paid' | 'overdue' | 'all';
    page?: number;
    perPage?: number;
    search?: string;
    customerName?: string;
    minAmount?: number;
    maxAmount?: number;
    startDate?: string;
    endDate?: string;
  }): Promise<{
    invoices: QBInvoice[];
    pagination: {
      total: number;
      page: number;
      perPage: number;
      hasMore: boolean;
    };
  }> {
    return this.makeRequest<{
      invoices: QBInvoice[];
      pagination: {
        total: number;
        page: number;
        perPage: number;
        hasMore: boolean;
      };
    }>('/api/quickbooks/invoices/', { params })
  }

  async getInvoice(id: string): Promise<{ invoice: QBInvoice }> {
    return this.makeRequest<{ invoice: QBInvoice }>(`/api/quickbooks/invoices/${id}/`)
  }

  async approveInvoice(id: string, data: {
    sendEmail: boolean;
    emailMessage?: string;
    ccEmails?: string[];
  }): Promise<{
    invoice: QBInvoice;
    emailSent: boolean;
    emailRecipients?: string[];
  }> {
    return this.makeRequest<{
      invoice: QBInvoice;
      emailSent: boolean;
      emailRecipients?: string[];
    }>(`/api/quickbooks/invoices/${id}/approve/`, {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async markInvoicePaid(id: string, data: {
    amount: number;
    paymentMethod: string;
    paymentDate: string;
    referenceNumber?: string;
    notes?: string;
  }): Promise<{
    invoice: QBInvoice;
    payment: {
      id: string;
      amount: number;
      date: string;
      method: string;
      referenceNumber?: string;
    };
  }> {
    return this.makeRequest<{
      invoice: QBInvoice;
      payment: {
        id: string;
        amount: number;
        date: string;
        method: string;
        referenceNumber?: string;
      };
    }>(`/api/quickbooks/invoices/${id}/payment/`, {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async getSyncStatus(): Promise<{
    connected: boolean;
    lastSync: string;
    nextSync: string;
    syncInProgress: boolean;
    errorCount: number;
    lastError?: {
      message: string;
      timestamp: string;
      code: string;
    };
    stats: {
      invoicesSynced: number;
      customersSynced: number;
      paymentsSynced: number;
    };
  }> {
    return this.makeRequest<{
      connected: boolean;
      lastSync: string;
      nextSync: string;
      syncInProgress: boolean;
      errorCount: number;
      lastError?: {
        message: string;
        timestamp: string;
        code: string;
      };
      stats: {
        invoicesSynced: number;
        customersSynced: number;
        paymentsSynced: number;
      };
    }>('/api/quickbooks/sync-status/')
  }

  async triggerManualSync(): Promise<{ message: string }> {
    return this.makeRequest<{ message: string }>('/api/quickbooks/sync/', {
      method: 'POST'
    })
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  async ensureConnection(): Promise<QBConnectionStatus> {
    const status = await this.getConnectionStatus();

    if (!status.is_connected) {
      throw new Error('QuickBooks connection required');
    }

    if (status.connection_status === 'needs_refresh') {
      throw new Error('QuickBooks connection expired - please reconnect');
    }

    return status;
  }
}